import { useState } from 'react';
import { GoogleAuthProvider, signInWithPopup, sendSignInLinkToEmail, type Auth } from 'firebase/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';

const GoogleLogo = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 48 48">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
);

interface LoginProps {
    auth: Auth;
}

export function Login({ auth }: LoginProps) {
    const [email, setEmail] = useState('');
    const [sent, setSent] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleGoogle = async () => {
        setError(null);
        try {
            await signInWithPopup(auth, new GoogleAuthProvider());
        } catch (e) {
            const err = e as { code?: string; message?: string } | null;
            if (err?.code !== 'auth/popup-closed-by-user') {
                setError(err?.message ?? String(e));
            }
        }
    };

    const handleEmailLink = async () => {
        if (!email) return;
        setError(null);
        setLoading(true);
        try {
            await sendSignInLinkToEmail(auth, email, {
                url: window.location.origin,
                handleCodeInApp: true,
            });
            window.localStorage.setItem('emailForSignIn', email);
            setSent(true);
        } catch (e) {
            const err = e as { message?: string } | null;
            setError(err?.message ?? String(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center">
            <div className="w-full max-w-sm space-y-4 px-4">
                <div className="text-center">
                    <h1 className="text-2xl font-semibold">Welcome</h1>
                </div>

                <Button type="button" variant="outline" className="w-full gap-3" onClick={handleGoogle}>
                    <GoogleLogo />
                    Continue with Google
                </Button>

                <div className="flex items-center gap-3">
                    <Separator className="flex-1" />
                    <span className="text-xs text-muted-foreground">or</span>
                    <Separator className="flex-1" />
                </div>

                {sent ? (
                    <div className="text-center space-y-3">
                        <p className="text-sm text-muted-foreground">
                            We sent a sign-in link to<br />
                            <span className="font-medium text-foreground">{email}</span>
                        </p>
                        <p className="text-sm text-muted-foreground">Check your inbox and click the link to continue.</p>
                        <button
                            type="button"
                            className="text-xs text-muted-foreground hover:underline"
                            onClick={() => { setSent(false); setError(null); }}
                        >
                            Try a different email
                        </button>
                    </div>
                ) : (
                    <form onSubmit={e => { e.preventDefault(); handleEmailLink(); }} className="space-y-3">
                        <Input
                            type="email"
                            placeholder="Email address"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            required
                        />
                        <Button type="submit" className="w-full" disabled={!email || loading}>
                            Continue
                        </Button>
                    </form>
                )}

                {error && <p className="text-sm text-destructive text-center">{error}</p>}
            </div>
        </div>
    );
}
